// src/components/admin/BlogManagement.jsx
import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Card,
  Button,
  Table,
  Modal,
  Form,
  Alert,
  Badge,
  Row,
  Col,
  ButtonGroup,
} from "react-bootstrap";
import PhotoUpload from "./PhotoUpload";

// Rich Text Editor Component with Enhanced Image Tools
const RichTextEditor = ({ value, onChange, propertyId }) => {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Image Picker State
  const [showPickerModal, setShowPickerModal] = useState(false);
  const [pickerPhotos, setPickerPhotos] = useState([]);
  const savedRange = useRef(null);

  // Sync external value changes to editor content
  useEffect(() => {
    if (
      editorRef.current &&
      value !== editorRef.current.innerHTML &&
      !isFocused
    ) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value, isFocused]);

  // Handle image selection inside the editor
  useEffect(() => {
    const HandleSelectionChange = () => {
      const selection = window.getSelection();
      if (!editorRef.current.contains(selection.anchorNode)) {
        setSelectedImage(null);
        return;
      }

      let targetImg = null;
      // Check if clicked directly on an image
      if (selection.anchorNode && selection.anchorNode.tagName === "IMG") {
        targetImg = selection.anchorNode;
      }
      // Check if selection is inside an image wrapper
      else if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const element = range.commonAncestorContainer.parentElement;
        if (element && element.tagName === "IMG") {
          targetImg = element;
        }
      }

      if (targetImg !== selectedImage) {
        // Remove highlight from prev image
        if (selectedImage) selectedImage.style.outline = "none";
        // Highlight new image
        if (targetImg) targetImg.style.outline = "3px solid #0d6efd";
        setSelectedImage(targetImg);
      }
    };

    document.addEventListener("selectionchange", HandleSelectionChange);
    // Clean up outlines on unmount
    return () => {
      document.removeEventListener("selectionchange", HandleSelectionChange);
      if (editorRef.current) {
        const images = editorRef.current.getElementsByTagName("img");
        for (let img of images) img.style.outline = "none";
      }
    };
  }, [selectedImage]);

  const triggerChange = () => {
    if (editorRef.current) {
      // Clean up outlines before saving
      if (selectedImage) selectedImage.style.outline = "none";
      const content = editorRef.current.innerHTML;
      if (selectedImage) selectedImage.style.outline = "3px solid #0d6efd"; // Restore for UI
      onChange(content);
    }
  };

  const executeCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    triggerChange();
    editorRef.current.focus();
  };

  // --- Image Alignment & Sizing ---

  const handleImageAlign = (alignment) => {
    if (!selectedImage) return;

    // Reset styles first
    selectedImage.style.float = "none";
    selectedImage.style.display = "inline-block";
    selectedImage.style.margin = "0";

    if (alignment === "left") {
      selectedImage.style.float = "left";
      selectedImage.style.margin = "0 15px 15px 0";
    } else if (alignment === "right") {
      selectedImage.style.float = "right";
      selectedImage.style.margin = "0 0 15px 15px";
    } else if (alignment === "center") {
      selectedImage.style.display = "block";
      selectedImage.style.margin = "15px auto";
    }

    triggerChange();
    editorRef.current.focus();
  };

  const handleImageResize = (change) => {
    if (selectedImage) {
      const currentHeight = selectedImage.offsetHeight;
      let newHeight = currentHeight + change;
      if (newHeight < 50) newHeight = 50;
      selectedImage.style.height = `${newHeight}px`;
      selectedImage.style.width = "auto";
      triggerChange();
    }
  };

  // --- Image Picker & Insertion Logic ---

  const saveSelection = () => {
    const selection = window.getSelection();
    if (
      selection.rangeCount > 0 &&
      editorRef.current.contains(selection.anchorNode)
    ) {
      savedRange.current = selection.getRangeAt(0);
    }
  };

  const restoreSelection = () => {
    if (savedRange.current) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedRange.current);
    }
  };

  const openImagePicker = () => {
    saveSelection();
    setPickerPhotos([]); // Reset picker state
    setShowPickerModal(true);
  };

  // Called by PhotoUpload when a new image is uploaded in the modal
  const handlePickerUpload = (newPhotos) => {
    // Find the newly added photo (the last one in the array)
    if (newPhotos.length > pickerPhotos.length) {
      const newPhoto = newPhotos[newPhotos.length - 1];
      insertImageAtCursor(newPhoto.url);
      setShowPickerModal(false);
    }
    setPickerPhotos(newPhotos);
  };

  const insertImageAtCursor = (url) => {
    restoreSelection();
    if (savedRange.current) {
      const img = document.createElement("img");
      img.src = url;
      // Default styles for a new image
      img.style.maxWidth = "100%";
      img.style.height = "auto";
      img.style.margin = "15px 0";
      img.style.display = "block"; // Default to block so it doesn't sit awkwardly in text

      savedRange.current.insertNode(img);
      savedRange.current.collapse(false); // Move cursor after image
      triggerChange();
    }
  };

  return (
    <>
      <div className="border rounded">
        {/* Toolbar */}
        <div
          className="bg-light border-bottom p-2 d-flex flex-wrap gap-2 align-items-center sticky-top"
          style={{ zIndex: 900, top: "55px" }}
        >
          <ButtonGroup size="sm" className="bg-white shadow-sm">
            <Button
              variant="outline-secondary"
              onClick={() => executeCommand("bold")}
              title="Bold"
            >
              <b>B</b>
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => executeCommand("italic")}
              title="Italic"
            >
              <i>I</i>
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => executeCommand("underline")}
              title="Underline"
            >
              <u>U</u>
            </Button>
          </ButtonGroup>

          <div className="vr mx-1"></div>

          <ButtonGroup size="sm" className="bg-white shadow-sm">
            <Button
              variant="outline-secondary"
              onClick={() => executeCommand("formatBlock", "H2")}
            >
              H2
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => executeCommand("formatBlock", "H3")}
            >
              H3
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => executeCommand("formatBlock", "P")}
            >
              P
            </Button>
          </ButtonGroup>

          <div className="vr mx-1"></div>

          <ButtonGroup size="sm" className="bg-white shadow-sm">
            <Button
              variant="outline-secondary"
              onClick={() => executeCommand("insertUnorderedList")}
              title="Bullet List"
            >
              • List
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => executeCommand("insertOrderedList")}
              title="Numbered List"
            >
              1. List
            </Button>
          </ButtonGroup>

          <div className="vr mx-1"></div>

          <Button
            size="sm"
            variant="outline-secondary"
            className="bg-white shadow-sm"
            onClick={() => {
              const url = prompt("Enter URL:");
              if (url) executeCommand("createLink", url);
            }}
          >
            🔗 Link
          </Button>

          {/* Insert Image Button opens Modal now */}
          <Button
            size="sm"
            variant="success"
            className="shadow-sm"
            onClick={openImagePicker}
            title="Insert Image at Cursor"
          >
            🖼️ Insert Image
          </Button>

          <Button
            size="sm"
            variant="outline-secondary"
            className="ms-auto"
            onClick={() => executeCommand("removeFormat")}
            title="Clear Formatting"
          >
            ✕ Clear Format
          </Button>

          {/* Contextual Image Toolbar - Appears on Selection */}
          {selectedImage && (
            <div className="w-100 mt-2 d-flex align-items-center gap-2 animate__animated animate__fadeIn">
              <Badge bg="primary">Image Selected</Badge>
              <ButtonGroup size="sm" className="bg-white shadow-sm">
                <Button
                  variant="outline-primary"
                  onClick={() => handleImageAlign("left")}
                  title="Float Left (Wrap Text)"
                >
                  ⇐ Float Left
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => handleImageAlign("center")}
                  title="Center"
                >
                  ⇔ Center
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => handleImageAlign("right")}
                  title="Float Right (Wrap Text)"
                >
                  ⇒ Float Right
                </Button>
              </ButtonGroup>
              <ButtonGroup size="sm" className="bg-white shadow-sm ms-2">
                <Button
                  variant="outline-primary"
                  onClick={() => handleImageResize(50)}
                  title="Increase Height"
                >
                  ↑ Bigger
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => handleImageResize(-50)}
                  title="Decrease Height"
                >
                  ↓ Smaller
                </Button>
              </ButtonGroup>
            </div>
          )}
        </div>

        {/* Editor Area */}
        <div
          ref={editorRef}
          className="p-3 bg-white"
          style={{
            minHeight: "400px",
            maxHeight: "650px",
            overflowY: "auto",
            outline: "none",
            fontSize: "16px",
            lineHeight: "1.75",
          }}
          contentEditable
          onInput={triggerChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          // Handle clicking directly on images
          onClick={(e) => {
            if (e.target.tagName !== "IMG" && selectedImage) {
              setSelectedImage(null);
            }
          }}
          onKeyUp={(e) => {
            if (
              selectedImage &&
              (e.key === "Backspace" || e.key === "Delete")
            ) {
              selectedImage.remove();
              setSelectedImage(null);
              triggerChange();
            }
          }}
          suppressContentEditableWarning={true}
        />
      </div>

      {/* Image Picker Modal */}
      <Modal
        show={showPickerModal}
        onHide={() => setShowPickerModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Upload Image to Insert</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="small mb-3">
            Upload a new image. It will be inserted automatically where your
            cursor was placed.
          </Alert>
          {/* Reuse PhotoUpload as a picker by listening to updates */}
          <PhotoUpload
            propertyId={propertyId || "temp-blog-picker"}
            photos={pickerPhotos}
            onPhotosUpdate={handlePickerUpload}
            previewHeight="150px"
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

const BlogManagement = ({
  posts = [],
  onAddPost,
  onUpdatePost,
  onDeletePost,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // generate a temporary ID for new posts for image storage
  const [tempPostId] = useState(`blog-${Date.now()}`);

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    author: "ACF Luxury Stays Team",
    publishedAt: new Date().toISOString().split("T")[0],
    image: "", // Hero image URL
    photos: [], // Helper for uploader
    isPublished: true,
    tags: "", // Comma separated tags
  });

  const resetForm = () => {
    setFormData({
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      author: "ACF Luxury Stays Team",
      publishedAt: new Date().toISOString().split("T")[0],
      image: "",
      photos: [],
      isPublished: true,
      tags: "",
    });
    setEditingPost(null);
    setError("");
  };

  const handleOpenModal = (post = null) => {
    if (post) {
      setEditingPost(post);
      setFormData({
        title: post.title || "",
        slug: post.slug || "",
        excerpt: post.excerpt || "",
        content: post.content || "",
        author: post.author || "ACF Luxury Stays Team",
        publishedAt: post.publishedAt || new Date().toISOString().split("T")[0],
        image: post.image || "",
        photos: post.image
          ? [{ url: post.image, filename: "Current Image" }]
          : [],
        isPublished: post.isPublished !== undefined ? post.isPublished : true,
        tags: post.tags ? post.tags.join(", ") : "",
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setTimeout(resetForm, 300);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      // Auto-generate slug from title if slug is empty or matches old title slug
      if (
        field === "title" &&
        (!prev.slug ||
          prev.slug === prev.title.toLowerCase().replace(/[^a-z0-9]+/g, "-"))
      ) {
        newData.slug = value.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      }

      return newData;
    });
  };

  const handlePhotoUpdate = (photos) => {
    setFormData((prev) => ({
      ...prev,
      photos,
      image: photos.length > 0 ? photos[0].url : "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.title || !formData.content) {
      setError("Title and Content are required");
      return;
    }

    try {
      setLoading(true);

      // Clean up data
      const cleanedData = {
        ...formData,
        tags: formData.tags
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t),
        slug:
          formData.slug ||
          formData.title.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
      };

      if (editingPost) {
        await onUpdatePost(editingPost.id, cleanedData);
      } else {
        await onAddPost(cleanedData);
      }

      handleCloseModal();
    } catch (err) {
      setError(err.message || "Failed to save post");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    try {
      setLoading(true);
      await onDeletePost(id);
    } catch (err) {
      alert("Failed to delete: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const currentStorageId = editingPost ? `blog-${editingPost.id}` : tempPostId;

  return (
    <div className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Blog Management</h2>
          <p className="text-muted mb-0">
            Create content to boost SEO and engage guests
          </p>
        </div>
        <Button variant="primary" onClick={() => handleOpenModal()}>
          + New Post
        </Button>
      </div>

      <Card>
        <Table responsive hover className="mb-0">
          <thead className="bg-light">
            <tr>
              <th>Date</th>
              <th>Title</th>
              <th>Author</th>
              <th>Status</th>
              <th style={{ width: "150px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {posts.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-4 text-muted">
                  No posts yet. Start writing!
                </td>
              </tr>
            ) : (
              posts.map((post) => (
                <tr key={post.id}>
                  <td>{new Date(post.publishedAt).toLocaleDateString()}</td>
                  <td>
                    <strong>{post.title}</strong>
                    <div className="small text-muted">/{post.slug}</div>
                  </td>
                  <td>{post.author}</td>
                  <td>
                    <Badge bg={post.isPublished ? "success" : "secondary"}>
                      {post.isPublished ? "Published" : "Draft"}
                    </Badge>
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => handleOpenModal(post)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDelete(post.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>

      {/* Edit/Create Modal */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="xl"
        backdrop="static"
        fullscreen="lg-down"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingPost ? "Edit Post" : "Create New Post"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}

            <Row>
              <Col lg={8}>
                <Form.Group className="mb-3">
                  <Form.Control
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    required
                    placeholder="Enter Post Title..."
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "bold",
                      border: "none",
                      boxShadow: "none",
                      paddingLeft: 0,
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Alert variant="light" className="py-2 small mb-2 border">
                    💡 <strong>How to align images:</strong> Click the "🖼️
                    Insert Image" button to upload. Once inserted,{" "}
                    <strong>click on the image</strong> in the text area to
                    reveal alignment (left/right wrap) and sizing controls.
                  </Alert>
                  <RichTextEditor
                    value={formData.content}
                    onChange={(content) =>
                      handleInputChange("content", content)
                    }
                    propertyId={currentStorageId}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Excerpt (SEO Description)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.excerpt}
                    onChange={(e) =>
                      handleInputChange("excerpt", e.target.value)
                    }
                    placeholder="Short summary for search engines and preview cards..."
                  />
                </Form.Group>
              </Col>

              <Col lg={4} className="border-start">
                <h6 className="mb-3">Post Settings</h6>
                <div className="mb-4">
                  <Form.Label>Featured Hero Image</Form.Label>
                  <PhotoUpload
                    propertyId={currentStorageId}
                    photos={formData.photos}
                    onPhotosUpdate={handlePhotoUpdate}
                    previewHeight="200px"
                    colProps={{ xs: 12 }}
                  />
                  <Form.Text className="text-muted small">
                    This is the main banner image shown at the top of the post.
                  </Form.Text>
                </div>

                <Card className="mb-3 bg-light border-0 shadow-sm">
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        value={formData.isPublished}
                        onChange={(e) =>
                          handleInputChange(
                            "isPublished",
                            e.target.value === "true",
                          )
                        }
                      >
                        <option value="true">Published</option>
                        <option value="false">Draft</option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Publish Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={formData.publishedAt}
                        onChange={(e) =>
                          handleInputChange("publishedAt", e.target.value)
                        }
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Author</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.author}
                        onChange={(e) =>
                          handleInputChange("author", e.target.value)
                        }
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Tags</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.tags}
                        onChange={(e) =>
                          handleInputChange("tags", e.target.value)
                        }
                        placeholder="e.g., San Diego, Tips"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>URL Slug</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.slug}
                        onChange={(e) =>
                          handleInputChange("slug", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-top-0">
            <Button variant="outline-secondary" onClick={handleCloseModal}>
              Discard
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={loading}
              size="lg"
              className="px-4"
            >
              {loading
                ? "Saving..."
                : editingPost
                  ? "Update Post"
                  : "Publish Post"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default BlogManagement;
